// How a reader's own screen was captured in an orientation call, so the
// aide's view and later analysis can tell a shared-tab recording from a DOM
// replay or from nothing at all. Persisted only as recording-file kinds: a
// call's mechanism is read back from the files the webhook indexed.

import * as z from "zod";

export const CAPTURE_MECHANISMS = ["tab_share", "dom_replay", "none"] as const;

export type CaptureMechanism = (typeof CAPTURE_MECHANISMS)[number];

export const CAPTURE_UNAVAILABLE_REASONS = [
  "consent_not_given",
  "session_ended",
  "recording_not_running",
  "not_connected",
  "aide_preview",
  "not_started",
  "no_get_display_media",
  "sdk_unsupported",
  "extension_required",
  "user_denied",
  "system_denied",
  "share_locked",
  "start_failed",
  "surface_not_tab",
  "surface_unknown",
  "stopped_by_user",
] as const;

export type CaptureUnavailableReason =
  (typeof CAPTURE_UNAVAILABLE_REASONS)[number];

export interface CaptureMechanismRecord {
  readonly mechanism: CaptureMechanism;
  /** Why tab sharing is not what ran; null while it runs. */
  readonly reason: CaptureUnavailableReason | null;
  /** The DOM replay reached a cap and dropped what came after it. */
  readonly capped?: true;
}

/** The `recordingType` a DOM replay's file is indexed under. */
export const DOM_REPLAY_RECORDING_TYPE = "dom_replay";

const DOM_REPLAY_CHUNK_ID_PREFIX = `${DOM_REPLAY_RECORDING_TYPE}:`;

// The chunk count stays well under MAX_RECORDING_FILES_PER_SESSION (200), so
// a withdrawal's file listing still reaches every one of Zoom's files too.
export const DOM_REPLAY_MAX_CHUNK_BYTES = 2 * 1024 * 1024;
export const DOM_REPLAY_MAX_SESSION_BYTES = 32 * 1024 * 1024;
export const DOM_REPLAY_MAX_CHUNKS = 150;
export const DOM_REPLAY_MAX_DURATION_MS = 60 * 60_000;

/** A replay chunk's id in the recording-file index: its capture and its place in it. */
export const domReplayChunkId = (captureId: string, seq: number): string =>
  `${DOM_REPLAY_CHUNK_ID_PREFIX}${captureId}:${seq}`;

/** Bounds of every replay chunk id, for a range read of one call's chunks. */
export const DOM_REPLAY_CHUNK_ID_RANGE = {
  from: DOM_REPLAY_CHUNK_ID_PREFIX,
  below: `${DOM_REPLAY_RECORDING_TYPE};`,
} as const;

// Zoom documents a participant's shared screen as `individual_shared_screen`
// in the session-recordings REST reply; the webhook payload's value is not
// documented separately, so any reader-keyed type naming a shared screen
// counts.
const isSharedScreenType = (recordingType: string): boolean =>
  recordingType.toLowerCase().includes("shared_screen");

export interface CaptureFileKind {
  readonly recordingType: string;
  readonly userKeyLabel: string;
}

/** The capture mechanisms a call's indexed recording files show for its reader. */
export function captureMechanismsOfFiles(
  files: readonly CaptureFileKind[],
): Exclude<CaptureMechanism, "none">[] {
  const found = new Set<Exclude<CaptureMechanism, "none">>();
  for (const file of files) {
    if (file.userKeyLabel !== "reader") continue;
    if (isSharedScreenType(file.recordingType)) found.add("tab_share");
    if (file.recordingType === DOM_REPLAY_RECORDING_TYPE) {
      found.add("dom_replay");
    }
  }
  return CAPTURE_MECHANISMS.filter(
    (mechanism): mechanism is Exclude<CaptureMechanism, "none"> =>
      mechanism !== "none" && found.has(mechanism),
  );
}

const CAPTURE_MESSAGE_KIND = "bindery.capture.v1";

const captureMessageSchema = z.object({
  kind: z.literal(CAPTURE_MESSAGE_KIND),
  mechanism: z.enum(CAPTURE_MECHANISMS),
  reason: z.enum(CAPTURE_UNAVAILABLE_REASONS).nullable(),
  capped: z.literal(true).optional(),
});

/** The reader's live report to the aide over Zoom's command channel. */
export function encodeCaptureMessage(record: CaptureMechanismRecord): string {
  return JSON.stringify({
    kind: CAPTURE_MESSAGE_KIND,
    mechanism: record.mechanism,
    reason: record.reason,
    ...(record.capped === true && { capped: true }),
  });
}

/** Null for any other command-channel traffic or a malformed report. */
export function parseCaptureMessage(
  text: string,
): CaptureMechanismRecord | null {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  const parsed = captureMessageSchema.safeParse(json);
  if (!parsed.success) return null;
  const { mechanism, reason, capped } = parsed.data;
  return capped === true
    ? { mechanism, reason, capped }
    : { mechanism, reason };
}
